/**
 * AnthemPage.jsx — "Soak It Up" easter-egg song page.
 * Route: /about/the-anthem
 * Public, indexable. Brand lore: TurfPulse is the only lawn-care SaaS with a theme song.
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Music, Disc3 } from 'lucide-react';

const TRACK_SPECS = [
  ['Tempo', '112 BPM'],
  ['Key', 'D minor (modulates up to E minor on the final chorus)'],
  ['Time', '4/4, swung 16ths on the hat'],
  ['Vibe', 'Bruno Mars × Mark Ronson — strut, swagger, finger-snap groove'],
  ['Length', '~3:20'],
];

const STAGES = [
  ['🥁 Kit', 'Linn LM-1 snare (gated, hard) · TR-808 kick · live tambourine on 2 & 4'],
  ['🎸 Bass', 'Slap bass — root + octave + ghost notes, locked to kick'],
  ['🎹 Keys', 'Clavinet (verse) · Rhodes pad (pre-chorus) · synth brass stabs (chorus)'],
  ['🎺 Horns', 'Trumpet + tenor sax + trombone — tight 3-part hits on the "1"'],
  ['🪘 Perc', 'Cowbell every 4 bars · congas under the bridge'],
  ['🎤 Vox', 'Lead in chest voice (D3-A4) · falsetto answer in the hook · gang "HEY!" backings'],
];

const SECTIONS = [
  {
    label: 'Intro · spoken',
    chords: '4-bar drum break + horn fanfare',
    lines: [
      '"Hah! Listen — your lawn been lyin\' to you, baby."',
      '"But the dirt? The dirt don\'t lie."',
      '"TurfPulse… let\'s go."',
    ],
    speak: true,
  },
  {
    label: 'Verse 1',
    chords: '| Dm9 | Dm9 | Gm7 | A7♯9 |  | Dm9 | Dm9 | Bb13  A7 | Dm9 |',
    lines: [
      'Sun comin\' up, you grabbin\' that hose 🌅',
      'Spray a little here, spray wherever it goes',
      'Neighbor on the left got a yellow patch',
      'Neighbor on the right got a water bill — catch!',
      '',
      'You been guessin\', you been stressin\', readin\' the grass',
      'Three bucks a gallon and it\'s leakin\' through fast',
      'But I got a little secret, lemme pull you aside —',
      'Three little probes and the truth\'s inside.',
    ],
  },
  {
    label: 'Pre-Chorus',
    chords: '| Bbmaj7 | C9 | Dm7 | Em7♭5  A7 |',
    lines: [
      'One in the dry, one in the mean,',
      'One in the wet — uh! — keep that data clean',
      'Hub on the wall got a Rachio kiss',
      'Soil say "go," and we don\'t miss…',
    ],
  },
  {
    label: 'Chorus 🎺',
    chords: '| Dm9 | Dm9 | Gm7 | A7♯9 |  | Dm9 | Dm9 | Bb13  A7 | Dm9 |',
    lines: [
      'Soak it up! (soak it up!)',
      'Let the dirt do the talkin\', baby —',
      'Soak it up! (soak it up!)',
      'TurfPulse got me feelin\' immortal, lately!',
      'No more guess, no more spray, no more flood —',
      'Just the data, just the green, just the good!',
      '(Hey!) Hey, hey — soak it up!',
      '(Hey!) Hey, hey — TurfPulse, what!',
    ],
    hook: true,
  },
  {
    label: 'Verse 2',
    chords: '(same groove)',
    lines: [
      'Two-eighty a month on a sprinkler that\'s blind?',
      'Sippin\' on margins while you losin\' your mind',
      'Round Rock rebate? I filed mine.',
      'Got a hundred fifty back and a smile on time',
      '',
      'Bermuda lookin\' lush, dollar spot? — declined ✋',
      'Smith-Kerns model keepin\' fungus offline',
      'Twelve percent floor and the TAMU stamp —',
      'Texas heat ain\'t breakin\' this camp.',
    ],
  },
  {
    label: 'Pre-Chorus (reprise)',
    chords: '| Bbmaj7 | C9 | Dm7 | Em7♭5  A7 |',
    lines: [
      'CH1, CH2, CH3 (yeah!)',
      'Buried in the ground like a dynasty',
      'Dewpoint stuck and the wind is right —',
      'Spray window open, baby — let\'s ignite!',
    ],
  },
  {
    label: 'Chorus',
    chords: '(repeat)',
    lines: ['(see chorus above — full band, gang vox)'],
    hook: true,
  },
  {
    label: 'Bridge 🎸',
    chords: '| Fmaj9 | Em7 | Dm9 | Gm7  C9 |   (half-time, wah guitar)',
    lines: [
      'Mmm — let me tell ya somethin\'…',
      'Rachio\'s the bridge — (yeah, yeah)',
      'But TurfPulse the brain — (c\'mon, c\'mon)',
      'You don\'t need a green thumb,',
      'Just a probe in the rain — (woo!)',
      '',
      'Inner Circle in the cut, crest on the chest 🛡️',
      'Fifty bucks a year and we built the rest',
      'No bots, no fluff, no salesman pitch —',
      'Just a regular guy with a regular itch.',
    ],
  },
  {
    label: 'Final Chorus · UP A STEP into Em9',
    chords: '| Em9 | Em9 | Am7 | B7♯9 |   (key lift)',
    lines: [
      'SOAK IT UP! (soak it up!)',
      'Let the dirt do the talkin\', baby —',
      'SOAK IT UP! (soak it up!)',
      'TURFPULSE GOT ME FEELIN\' IMMORTAL, LATELY!',
      '(Hey!) No more brown — (Hey!) no more drown —',
      '(Hey!) Just the greenest yard in the whole damn town!',
      'Soak it up — TurfPulse, WHAT!',
    ],
    hook: true,
  },
  {
    label: 'Outro',
    chords: '(cowbell + tambourine fade · last horn stab on beat 1)',
    lines: [
      '"That\'s the sound of a lawn that knows what it\'s doin\', baby…"',
      '"…TurfPulse. We don\'t guess. We measure."',
    ],
    speak: true,
  },
];

const AnthemPage = () => {
  const [embedUrl, setEmbedUrl] = useState('');

  return (
    <>
      <Helmet>
        <title>Soak It Up — The TurfPulse Anthem</title>
        <meta
          name="description"
          content="A funk anthem for probe-driven irrigation. Lyrics, chord chart, beat map, and the full track for the TurfPulse Inner Circle."
        />
        <meta property="og:title" content="Soak It Up — The TurfPulse Anthem" />
        <meta
          property="og:description"
          content="The only lawn-care SaaS with a theme song. Full Bruno-Mars-style funk track about probe-driven irrigation."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-emerald-50 dark:from-zinc-950 dark:via-amber-950/30 dark:to-emerald-950/40 pb-16">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="mb-6">
            <Button variant="ghost" size="sm" asChild data-testid="anthem-back-button">
              <Link to="/about">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to About
              </Link>
            </Button>
          </div>

          {/* Hero */}
          <div className="text-center mb-10" data-testid="anthem-hero">
            <Badge className="bg-amber-500 hover:bg-amber-600 text-amber-950 mb-4 uppercase tracking-widest text-[10px]">
              <Disc3 className="w-3 h-3 mr-1" /> The Anthem · Easter Egg
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight bg-gradient-to-r from-amber-600 via-rose-500 to-emerald-600 bg-clip-text text-transparent">
              Soak It Up
            </h1>
            <p className="mt-3 text-lg md:text-xl text-muted-foreground italic">
              A funk anthem for probe-driven irrigation
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              In the spirit of <em>Uptown Funk</em> meets <em>24K Magic</em> · Written for TurfPulse
              by a regular guy who probes his own dirt
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/about/the-songbook#soak-it-up"
                className="underline text-emerald-700 dark:text-emerald-300 hover:text-emerald-500"
                data-testid="anthem-songbook-link"
              >
                🎵 This song is now Track 03 of the TurfPulse Songbook · view all 5 songs →
              </Link>
            </p>
          </div>

          {/* Embed slot */}
          <Card className="mb-8 border-2 border-amber-300 dark:border-amber-700 shadow-lg" data-testid="anthem-embed-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="w-5 h-5 text-amber-600" /> Listen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {embedUrl ? (
                <div className="aspect-video w-full">
                  <iframe
                    title="Soak It Up — TurfPulse Anthem"
                    src={embedUrl}
                    className="w-full h-full rounded-md"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    allowFullScreen
                    data-testid="anthem-embed-iframe"
                  />
                </div>
              ) : (
                <div className="text-center py-10 px-4 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-md bg-amber-50/50 dark:bg-amber-950/20">
                  <Disc3 className="w-12 h-12 mx-auto mb-3 text-amber-500 animate-pulse" />
                  <p className="font-semibold mb-1" data-testid="anthem-embed-placeholder">
                    Track lands here once it's mastered.
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Paste a Suno / Udio / YouTube / Spotify embed URL below to mount the player.
                  </p>
                  <input
                    type="url"
                    placeholder="https://suno.com/embed/… or https://www.youtube.com/embed/…"
                    className="w-full max-w-md mx-auto px-3 py-2 rounded-md border bg-background text-sm"
                    onChange={(e) => setEmbedUrl(e.target.value.trim())}
                    data-testid="anthem-embed-url-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Track specs */}
          <Card className="mb-6" data-testid="anthem-specs-card">
            <CardHeader>
              <CardTitle>🎚 Track Specs</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {TRACK_SPECS.map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b py-1">
                    <dt className="font-semibold">{k}</dt>
                    <dd className="text-muted-foreground text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          {/* Production map */}
          <Card className="mb-6" data-testid="anthem-production-card">
            <CardHeader>
              <CardTitle>🎬 Production Map</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {STAGES.map(([k, v]) => (
                <div key={k} className="flex flex-col md:flex-row md:gap-4">
                  <div className="font-semibold w-32 shrink-0">{k}</div>
                  <div className="text-muted-foreground">{v}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Lyrics + chords */}
          <div className="space-y-5" data-testid="anthem-lyrics-section">
            <h2 className="text-3xl font-bold tracking-tight mt-2">🎤 Lyrics &amp; Chords</h2>
            {SECTIONS.map((sec, i) => (
              <Card
                key={i}
                className={
                  sec.hook
                    ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20'
                    : ''
                }
                data-testid={`anthem-section-${i}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base font-bold uppercase tracking-wide">
                      {sec.label}
                    </CardTitle>
                    {sec.chords && (
                      <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {sec.chords}
                      </code>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div
                    className={`whitespace-pre-line leading-relaxed ${
                      sec.speak ? 'italic text-muted-foreground' : ''
                    } ${sec.hook ? 'font-semibold text-base md:text-lg' : 'text-sm md:text-base'}`}
                  >
                    {sec.lines.join('\n')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-12 text-center text-sm text-muted-foreground" data-testid="anthem-footer-note">
            <p className="mb-2">
              Built by Derwin · The only lawn-care SaaS with a theme song · 🛡️ Inner Circle
            </p>
            <Link to="/" className="underline hover:text-emerald-600">
              Back to TurfPulse →
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

export default AnthemPage;
